import produce from 'immer';
import reactUpdate from 'immutability-helper';
import React, {UpdateSpecPath} from 'react';
import _ from 'underscore';
import {v4 as uuidv4} from 'uuid';

import {assert} from 'Asserts';
import {ResultsFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import BenchlingConfig from 'BenchlingConfig';
import {ContextMenuOption} from 'components/ContextMenu';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {Batch} from 'constants/SampleConstants';
import {BioentitySummary} from 'folder-items/FileConstants';
import deepCopy from 'lib/deepCopy';
import {
  getUnusedSchemaFields,
  isFieldTypeSupportedForManualEntry,
  prepareDataTableConfigForStore,
} from 'notebook/data-tables/DataTableConfigEditUtil';
import {
  DATA_TABLE_TYPE,
  DataTable,
  DataTableColumn,
  DataTableConfig,
  DataTableDataKey,
  DEFAULT_DEFAULT_VALUE,
  FromServerDataTable,
  FromServerDataTableConfig,
  Row,
  ToServerDataTable,
  ToServerDataTableConfig,
} from 'notebook/data-tables/DataTableConstants';
import NoSampleLabel from 'notebook/data-tables/NoSampleLabel';
import {ClientId} from 'notebook/ELNConstants';
import {CidIdMap} from 'notebook/notes/NoteConstants';
import {getColumnLabel} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {toSchemaFieldColumnDefinition} from 'notebook/structured-tables/columns/toSchemaFieldColumnDefinition';
import {
  BaseTagValueStructuredTableColumn,
  TagValueStructuredTableColumn,
} from 'notebook/structured-tables/StructuredTableConstants';
import fillFormulaDefault from 'notebook/structured-tables/util/fillFormulaDefault';
import {makeSchemaFieldStructuredColumn} from 'notebook/structured-tables/util/StructuredTableColumnUtil';
import {CELL_UI_PROPERTIES, PLACEHOLDER_CELL} from 'notebook/tables/constants/CellConstants';
import {Cells, ROW_UI_PROPERTIES, RowData} from 'notebook/tables/constants/RowConstants';
import {
  ColumnConstraint,
  InsertableColumnInfo,
  TableColumnType,
  TooltipIcon,
} from 'notebook/tables/constants/TableColumnConstants';
import {Table, TableData} from 'notebook/tables/constants/TableConstants';
import {SelectionRange, TableDimension} from 'notebook/tables/constants/TableSelectionConstants';
import {SimpleTableAxisCell} from 'notebook/tables/core-ui/TableAxisCell';
import Excess from 'notebook/tables/excess';
import {
  EvaluatedCell,
  EvaluatedTable,
  FullTableMap,
  OutputTableMap,
} from 'notebook/tables/excess/EvaluationConstants';
import {
  insertColumnsLeft,
  insertRowsAbove,
  wrapTableOpForSingleDoc,
} from 'notebook/tables/excess/ops/TableOps';
import TableUtil from 'notebook/tables/TableUtil';
import {
  convertLegacyCell,
  formatTypedValue,
  toStringTypedValue,
} from 'notebook/tables/typed-values/DataTypes';
import {hasNLO} from 'notebook/TypePredicates';
import {Note, NoteLinkedObjectType} from 'notebook/util/NoteUtilConstants';
import {ASSAY_TYPES} from 'results/assay-schemas/AssaySchemaConstants';
import {FIELD_TYPES, SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {getFieldForColumn} from 'schemas/fields/SchemaFieldGetters';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';
import {getFieldTypeName} from 'tag-schema/util/TagSchemaUtil';
import BioentityChip from 'ui-library/chips/BioentityChip';
import {ChipSize} from 'ui-library/chips/Chip';
import {omit} from 'util/omitKey';
import uniqueId from 'util/uniqueId';
import * as StageRunUtil from 'workflows/util/StageRunUtil';
import {SAMPLE_COLUMN_LABEL, StageRun, StageRunSample} from 'workflows/WorkflowConstants';

const LINKED_ID_COLUMN_WIDTH = 200;

export function isLinkTypeBatch(dataTableConfig: DataTableConfig): boolean {
  return dataTableConfig.linkType === FIELD_TYPES.BATCH_LINK.type;
}

export function isLinkTypeFile(dataTableConfig: DataTableConfig): boolean {
  return dataTableConfig.linkType === FIELD_TYPES.BIOENTITY_LINK.type;
}

export function isAssayTypeResult(dataTableConfig: DataTableConfig): boolean {
  return dataTableConfig.assaySchema.schemaType === ASSAY_TYPES.ASSAY_RESULT;
}

export function getDataTableConfigType(dataTableConfig: DataTableConfig): DATA_TABLE_TYPE {
  if (!dataTableConfig.isStandalone) {
    assert(
      isLinkTypeBatch(dataTableConfig) || isLinkTypeFile(dataTableConfig),
      'I/O DataTable requires schema w/ batch or file'
    );
    return DATA_TABLE_TYPE.STAGE_RUN_SAMPLE_LINK_TABLE;
  } else if (isLinkTypeBatch(dataTableConfig)) {
    return DATA_TABLE_TYPE.BATCH_LINK_TABLE;
  } else if (isLinkTypeFile(dataTableConfig)) {
    return DATA_TABLE_TYPE.FILE_LINK_TABLE;
  } else {
    return DATA_TABLE_TYPE.NO_LINK_TABLE;
  }
}

export function updateDataTableConfigName(
  dataTableConfig: DataTableConfig,
  nameFn: (name: string) => string
): DataTableConfig {
  return reactUpdate(dataTableConfig, {name: {$apply: nameFn}});
}

/**
 * Helper to clone a data table to be re-used in the same entry. We ensure that cids are regenerated
 * and assume that none of the euids are already present in the entry.
 */
export function cloneDataTable(dataTable: DataTable): DataTable {
  return {
    ...dataTable,
    cid: uniqueId('dt'),
  };
}

export function getDataTableEuids(dataTable: DataTable): Array<string> {
  return dataTable.rows.map((row) => row.euid);
}

export function getEntryEuids(note: Note): Array<string> {
  const resultEuids = [];
  for (const notePart of note.noteParts) {
    if (hasNLO(notePart) && notePart.item.type === NoteLinkedObjectType.DATA_TABLE) {
      resultEuids.push(...getDataTableEuids(notePart.item as DataTable));
    }
  }
  return resultEuids;
}

/**
 * Helper to insert rows into a table at a given row index, handling translation of any formulas in the
 * default values of the config columns
 *
 */
export function generateDataTableRow(
  config: DataTableConfig,
  rowIndex: number,
  dataKey: DataTableDataKey
): Row {
  return {
    ...dataKey,
    euid: uuidv4(),
    cells: config.columns.map((column, j) =>
      reactUpdate(column.defaultValue, {
        text: {
          $apply: (text: string) => {
            // First row of data table config contains default values,
            // while new row will need to translate formulas to the current row index
            const rel = Excess.tokenizer.toRelativeFormula(text, {row: 0, column: j});
            const abs = Excess.tokenizer.toAbsoluteFormula(rel, {row: rowIndex, column: j});
            return abs;
          },
        },
      })
    ),
  };
}

/**
 * Return an immutable copy of the given data table with the given row added at the bottom.
 */
export function addRowToDataTable(dataTable: DataTable, row: Row): DataTable {
  return reactUpdate(dataTable, {rows: {$push: [row]}} as UpdateSpecPath);
}

/**
 * Return an immutable copy of the given data table with a new row linked to the given stage run and
 * stage run sample.
 *
 * The row is added at the bottom of the table.
 */
export function addRowToStageRunSampleTable(
  dataTable: DataTable,
  stageRunId: string,
  stageRunSampleId: string
): DataTable {
  const newRowIndex = dataTable.rows.length;
  const newRow = generateDataTableRow(dataTable.config, newRowIndex, {stageRunId, stageRunSampleId});
  return addRowToDataTable(dataTable, newRow);
}

/**
 * Same as above, but in this case, we're adding a row to a standalone data table. The row
 * must include either a `batchId` or a `fileId`.
 */
export function addRowToStandaloneSampleTable(dataTable: DataTable, dataKey: DataTableDataKey): DataTable {
  const newRowIndex = dataTable.rows.length;
  const newRow = generateDataTableRow(dataTable.config, newRowIndex, dataKey);
  return addRowToDataTable(dataTable, newRow);
}

export function addRowsToStageRunSampleTable(
  dataTable: DataTable,
  newRowKeys: Array<{stageRunId: string; stageRunSampleId: string}>
): DataTable {
  let newDataTable = dataTable;
  for (const {stageRunId, stageRunSampleId} of newRowKeys) {
    newDataTable = addRowToStageRunSampleTable(newDataTable, stageRunId, stageRunSampleId);
  }
  return newDataTable;
}

export function updateNoteWithDataTables(
  note: Note,
  newDataTables: Array<DataTable>
): {hadChanges: boolean; newNote: Note} {
  let hadChanges = false;

  // eslint-disable-next-line no-restricted-syntax
  const dataTableCids = _.pluck(newDataTables, 'cid');
  const newNote = {
    ...note,
    noteParts: note.noteParts.map((notePart) => {
      if (
        hasNLO(notePart) &&
        notePart.item.type === NoteLinkedObjectType.DATA_TABLE &&
        dataTableCids.includes(notePart.item.cid)
      ) {
        hadChanges = true;
        const newDataTable = _.findWhere(newDataTables, {cid: notePart.item.cid});
        assert(newDataTable != null, `No data table found with cid ${notePart.item.cid}`);
        return reactUpdate(notePart, {
          item: {$set: newDataTable},
        });
      } else {
        return notePart;
      }
    }),
  };
  return {hadChanges, newNote};
}

/**
 * Don't allow creating data tables when we haven't loaded all samples yet.
 */
export function isDataTableCreationAllowed(
  stageRuns: Array<StageRun>,
  stageRunSamplesById: {[stageRunSampleId: string]: StageRunSample}
): boolean {
  return stageRuns.every(
    (stageRun) =>
      stageRun.inputSampleIds.every((id) => stageRunSamplesById[id] != null) &&
      stageRun.outputSampleIds.every((id) => stageRunSamplesById[id] != null)
  );
}

function _createDataTableRows(dataTableConfig: DataTableConfig): Array<Row> {
  const rows = [];
  if (!dataTableConfig.linkType) {
    rows.push(generateDataTableRow(dataTableConfig, rows.length, {}));
  }
  return rows;
}

/**
 * Ceate a data table from an already prepared data table config in a store-friendly representation,
 * i.e. `dataTableConfig` has ids replaced with cids.
 *
 * Note that the schema that is used is stored in its entirety on the config itself where every column's
 * reference to a schema field is captured via the field's cid.
 */
export function createDataTable(
  dataTableConfig: DataTableConfig,
  {noRows = false}: {noRows?: boolean} = {}
): DataTable {
  return {
    cid: uniqueId('dt'),
    provenanceUuid: uuidv4(),
    type: NoteLinkedObjectType.DATA_TABLE,
    config: dataTableConfig,
    rows: noRows ? [] : _createDataTableRows(dataTableConfig),
  };
}

export function isDataTableFeatureEnabled(): boolean {
  return (
    BenchlingConfig.LEGACY_WORKFLOWS_ENABLE || isFeatureEnabled({feature: ResultsFeature.RESULTS_CREATE})
  );
}

/**
 * @param fileType Type of file, i.e. entry, template, stage entry, document
 * @param isConfig Boolean indicating if this file is a template/config or executable
 * @returns Returns a boolean indicating whether the data table is supported for the given file
 */
export function isDataTableSupportedForFileType(fileType: string, isConfig: boolean = true): boolean {
  switch (fileType) {
    case ModelTypeConstants.STAGE_ENTRY:
      return true;
    case ModelTypeConstants.DOCUMENT:
      return !isConfig && isFeatureEnabled({feature: ResultsFeature.RESULTS_CREATE});
    case ModelTypeConstants.ENTRY:
      return isFeatureEnabled({feature: ResultsFeature.RESULTS_CREATE});
    default:
      return false;
  }
}

/**
 * @param fileType Type of file, i.e. entry, template, stage entry, document
 * @param isConfig Boolean indicating if this file is a template/config or executable
 * @returns Returns a boolean indicating whether the config data table is supported for the given file
 */
export function isDataTableConfigSupportedForFileType(fileType: string, isConfig: boolean = false): boolean {
  switch (fileType) {
    case ModelTypeConstants.STAGE_CONFIGURATION:
      return true;
    case ModelTypeConstants.SUBTEMPLATE:
      if (!BenchlingConfig.ELN_ENABLE_INSERT_NEW_RESULTS_TABLE) {
        // Legacy results tables (data tables) not supported in sub-templates
        return false;
      }
      return isFeatureEnabled({feature: ResultsFeature.RESULTS_CREATE});
    case ModelTypeConstants.TEMPLATE:
      return isFeatureEnabled({feature: ResultsFeature.RESULTS_CREATE});
    case ModelTypeConstants.DOCUMENT:
      return isConfig && isFeatureEnabled({feature: ResultsFeature.RESULTS_CREATE});
    default:
      return false;
  }
}

export function getDataTableConfigName(dataTableConfig: DataTableConfig): string {
  return dataTableConfig.name;
}

export function getDataTableName(dataTable: DataTable): string {
  return getDataTableConfigName(dataTable.config);
}

/**
 * Given a DataTableConfig, return the React node to use for the ID column.
 */
export function tableSampleIdHeader(config: DataTableConfig): JSX.Element | null {
  const sampleIdHeader = tableSampleIdHeaderText(config);
  if (sampleIdHeader == null) {
    return null;
  }
  // As we're embedding this AxisCell in a list, we need a key which can just be
  // a stable identifier as this cell won't change.
  const key = 'stx-0';
  return (
    <SimpleTableAxisCell
      key={key}
      index={0}
      isLeftAlign={false}
      dim={TableDimension.COLUMN}
      label={sampleIdHeader}
      numDims={config.columns.length + 1}
    />
  );
}

/**
 * Given a DataTableConfig, return the label we should use for ID column.
 */
export function tableSampleIdHeaderText(config: DataTableConfig): string | null {
  if (!config.linkType) {
    return null;
  }
  return SAMPLE_COLUMN_LABEL;
}

function getColumnIcons(column: DataTableColumn, indicateCustomColumns: boolean): Array<TooltipIcon> {
  const tooltipIcons: Array<TooltipIcon> = [];
  if (!column.isReported) {
    tooltipIcons.push({
      icon: 'eye-slash',
      tooltip: 'Not Reported',
    });
  }
  if (indicateCustomColumns && column.tagSchemaFieldId == null) {
    tooltipIcons.push({
      icon: 'asterisk',
      tooltip: 'Custom Column (text)',
    });
  }
  return tooltipIcons;
}

export function markColumnAsReported(
  table: TableData,
  selectionRange: SelectionRange,
  isReported: boolean
): TableData {
  return reactUpdate(table, {
    columnConstraints: {
      $apply: (columnConstraints: Array<ColumnConstraint>) =>
        columnConstraints.map((columnConstraint, i) =>
          i >= selectionRange.minIndex && i <= selectionRange.maxIndex
            ? reactUpdate(columnConstraint, {
                structuredTableColumn: {$merge: {isReported}},
              })
            : columnConstraint
        ),
    },
  });
}

export function getDataTableConfigContextMenuOptions(
  dataTableConfig: DataTableConfig,
  {
    onMarkAsReported,
  }: {
    onMarkAsReported?: null | ((selectionRange: SelectionRange, hasNotReported: boolean) => void);
  }
): (selectionRange: SelectionRange | null) => Array<ContextMenuOption> | null {
  // Return a function from selectionRange to the dataTableConfig specific context menu options
  return (selectionRange: SelectionRange | null) => {
    if (selectionRange == null) {
      return null;
    }
    const options = [];
    if (!dataTableConfig.isStandalone && onMarkAsReported) {
      const hasNotReported = dataTableConfig.columns
        .slice(selectionRange.minIndex, selectionRange.maxIndex + 1)
        .some((column) => !column.isReported);
      options.push({
        key: 'reported-option',
        name: hasNotReported ? 'Mark as reported' : 'Mark as not reported',
        click: () => {
          onMarkAsReported(selectionRange, hasNotReported);
        },
      });
    }
    return options;
  };
}

function _getColumnTooltip(
  dataTableConfig: DataTableConfig,
  column: DataTableColumn,
  {columnTypesByCid}: {columnTypesByCid?: {[cid: string]: string}}
): string | null {
  let fieldType = null;
  if (columnTypesByCid != null) {
    fieldType = columnTypesByCid[column.cid];
    if (fieldType) {
      return `${column.name} (${getFieldTypeName(fieldType)})`;
    }
  }

  const field = getFieldForColumn(dataTableConfig.assaySchema, column);
  return field ? `${field.displayName} (${getFieldTypeName(field.fieldType)})` : null;
}

function _getRenderedDataTableName(dataTableConfig: DataTableConfig): JSX.Element | string {
  if (dataTableConfig.name.startsWith(dataTableConfig.assaySchema.name)) {
    return dataTableConfig.name;
  }
  return (
    <span>
      {dataTableConfig.name} <span className="u-faded">({dataTableConfig.assaySchema.name})</span>
    </span>
  );
}

export function convertLegacyCellsInTable(table: Cells, columnConstraints: Array<ColumnConstraint>): Cells {
  return table.map((tableRow) =>
    tableRow.map((tableCell, columnIndex) => {
      const columnConstraint = columnConstraints[columnIndex];
      if (columnConstraint.tagSchemaField) {
        const schemaFieldType = columnConstraint.tagSchemaField.fieldType;
        return convertLegacyCell(tableCell, schemaFieldType);
      } else if (
        columnConstraint.columnDefinition &&
        columnConstraint.columnDefinition.type === TableColumnType.TAG_VALUE
      ) {
        const schemaFieldType = columnConstraint.columnDefinition.tagSchemaFieldType;
        return convertLegacyCell(tableCell, schemaFieldType);
      } else {
        return tableCell;
      }
    })
  );
}

/**
 * Convert a data table config's columns into TableEditable's columnConstraint format.
 *
 * For schema field columns, use column definitions if supported; otherwise, fall back to the old
 * columnConstraint.tagSchemaField system.
 */
export function getColumnConstraintsForTable(
  dataTableConfig: DataTableConfig,
  {
    alwaysAllowColumnRename,
    columnTypesByCid,
    indicateCustomColumns,
    isPlaceholder,
    viewer,
  }: {
    alwaysAllowColumnRename?: boolean;
    columnTypesByCid?: {[cid: string]: string};
    indicateCustomColumns: boolean;
    isPlaceholder?: boolean;
    viewer?: boolean;
  }
): Array<ColumnConstraint> {
  const {columns} = dataTableConfig;

  const columnConstraints: Array<ColumnConstraint> = [];
  for (const column of columns) {
    const schemaField = getFieldForColumn(dataTableConfig.assaySchema, column);
    const columnDefinition = schemaField
      ? toSchemaFieldColumnDefinition(schemaField, column as BaseTagValueStructuredTableColumn<ClientId>)
      : null;
    const columnConstraint: ColumnConstraint = {
      structuredTableColumn: column,
      tooltip: _getColumnTooltip(dataTableConfig, column, {columnTypesByCid}),
      canResetLabel: column.tagSchemaFieldId != null,
      editLabelDisabled: !alwaysAllowColumnRename && column.tagSchemaFieldId != null,
      tooltipIcons: getColumnIcons(column, indicateCustomColumns),
      viewer,
      // If the table is a placeholder, including the column definition would result in meaningless
      // validation errors on the empty dummy row
      columnDefinition: isPlaceholder ? null : columnDefinition,
      // But, we still want to use the column definition to generate the proper column label no
      // matter what
      renderedLabel: columnDefinition ? getColumnLabel(columnDefinition) : column.name,
      isProtected: column.isProtected,
    };
    columnConstraints.push(columnConstraint);
  }

  return columnConstraints;
}

function _convertToTable(
  dataTableConfig: DataTableConfig,
  {
    table,
    rowData,
    rowLabels,
    allowRowRemoval,
    allowNoRows,
    alwaysAllowColumnRename,
    columnTypesByCid,
    indicateCustomColumns,
    isPlaceholder,
    viewer,
    provenanceUuid,
  }: {
    table: Cells;
    rowData?: Array<RowData>;
    rowLabels: Array<JSX.Element | string> | null;
    allowRowRemoval: boolean;
    allowNoRows: boolean;
    alwaysAllowColumnRename?: boolean;
    columnTypesByCid?: {[cid: string]: string};
    indicateCustomColumns: boolean;
    isPlaceholder?: boolean;
    viewer?: boolean;
    provenanceUuid?: string | null;
  }
): Table & {columnConstraints: Array<ColumnConstraint>} {
  const {columns} = dataTableConfig;
  const columnConstraints = getColumnConstraintsForTable(dataTableConfig, {
    alwaysAllowColumnRename,
    columnTypesByCid,
    indicateCustomColumns,
    isPlaceholder,
    viewer,
  });

  return {
    name: getDataTableConfigName(dataTableConfig),
    renderedName: _getRenderedDataTableName(dataTableConfig),
    headerRow: false,
    headerCol: false,
    // TODO(BNCH-7596): Once `columnLabels` is no longer being used to populate the column name that
    // is sent to the server, we can set the value of `columnLabels` using `getColumnLabels`
    columnLabels: columns.map((column) => column.name),
    rowLabels,
    lockedDims: true,
    allowColumnEdits: true,
    allowRowRemoval,
    allowNoRows,
    widths: columns.map((column) => column.width),
    table: convertLegacyCellsInTable(table, columnConstraints),
    rowData,
    columnConstraints,
    _firstColumnWidth: dataTableConfig.linkType ? LINKED_ID_COLUMN_WIDTH : null,
    _rowHeader: tableSampleIdHeader(dataTableConfig),
    _tableType: ModelTypeConstants.DATA_TABLE,
    provenanceUuid,
  };
}

function getPlaceholderRowLabel(dataTableConfig: DataTableConfig): string {
  if (isLinkTypeBatch(dataTableConfig)) {
    return 'No batches';
  } else if (isLinkTypeFile(dataTableConfig)) {
    return 'No entities';
  } else {
    return '';
  }
}

export function convertDataTableToTable(
  dataTable: DataTable,
  rowLabels: Array<JSX.Element | string> | null = null,
  viewer: boolean = false
): Table & {columnConstraints: Array<ColumnConstraint>} {
  const {columns} = dataTable.config;

  let rowData = dataTable.rows.map((row) => omit(row, 'cells'));
  let table = dataTable.rows.map((row, i) =>
    row.cells.map((cell, j) => fillFormulaDefault(cell, columns, i, j))
  );
  if (dataTable.rows.length === 0) {
    rowLabels = [getPlaceholderRowLabel(dataTable.config)];
    table = [columns.map((column, i) => PLACEHOLDER_CELL)];
    rowData = [];
  }
  return _convertToTable(dataTable.config, {
    table,
    rowData,
    rowLabels,
    allowRowRemoval: true,
    allowNoRows: Boolean(dataTable.config.linkType),
    indicateCustomColumns: true,
    isPlaceholder: dataTable.rows.length === 0,
    viewer,
    provenanceUuid: dataTable.provenanceUuid,
  });
}

export function getRowLabelsForStageEntryDataTable(
  dataTable: DataTable,
  stageRuns: Array<StageRun> = [],
  stageRunSampleById: {[stageRunSampleId: string]: StageRunSample} = {}
): Array<JSX.Element> {
  return dataTable.rows.map(({stageRunId, stageRunSampleId}) => {
    const stageRun = _.findWhere(stageRuns, {id: stageRunId}) || null;
    const stageRunSample = stageRunSampleId ? stageRunSampleById[stageRunSampleId] : null;
    return <StageRunUtil.StageRunSampleRowLabel stageRun={stageRun} stageRunSample={stageRunSample} />;
  });
}

export function getSampleIdsForStageEntryDataTable(
  dataTable: DataTable,
  stageRunSampleById: {[stageRunSampleId: string]: StageRunSample} = {}
): Array<string> {
  return dataTable.rows.map(({stageRunSampleId}) => {
    const stageRunSample = stageRunSampleId ? stageRunSampleById[stageRunSampleId] : null;
    return stageRunSample ? stageRunSample.name : 'Loading';
  });
}

function _getLabelText(item: BioentitySummary | Batch | null): string {
  if (item == null) {
    return 'unavailable';
  }

  if ('displayLabel' in item) {
    return item.displayLabel;
  } else {
    return item.name;
  }
}

export function getRowLabelsForBatchDataTable(dataTable: DataTable): Array<JSX.Element> {
  return dataTable.rows.map(({batchId}) => (batchId ? <span>{batchId}</span> : <NoSampleLabel />));
}

export function getSampleIdsForBatchDataTable(
  dataTable: DataTable,
  batchesById: {[batchId: string]: Batch}
): Array<string> {
  return dataTable.rows.map(({batchId}) => _getLabelText(batchId ? batchesById[batchId] : null));
}

export function getRowLabelsForFileDataTable(dataTable: DataTable): Array<JSX.Element> {
  return dataTable.rows.map(({fileId}) =>
    fileId ? <BioentityChip id={fileId} size={ChipSize.TABLE_CELL} /> : <NoSampleLabel />
  );
}

export function getSampleIdsForFileDataTable(
  dataTable: DataTable,
  fileSummariesById: {[bioentityId: string]: BioentitySummary}
): Array<string> {
  return dataTable.rows.map(({fileId}) => _getLabelText(fileId ? fileSummariesById[fileId] : null));
}

interface ColumnNameAndValueExtractor {
  name: string;
  extractValue: (dataTableRow: Row) => string;
}

function _getDataTableColumnsToAffixForExport({
  tableType,
  batchesById,
  fileSummariesById,
  stageRuns,
  stageRunSampleById,
}: {
  tableType: string;
  batchesById: {[batchId: string]: Batch};
  fileSummariesById: {[bioentityId: string]: BioentitySummary};
  stageRuns?: Array<StageRun>;
  stageRunSampleById?: {[stageRunSampleId: string]: StageRunSample};
}): Array<ColumnNameAndValueExtractor> {
  let columnsToAffix: Array<ColumnNameAndValueExtractor>;
  if (tableType === DATA_TABLE_TYPE.STAGE_RUN_SAMPLE_LINK_TABLE) {
    if (!stageRuns || !stageRunSampleById) {
      return [];
    }
    columnsToAffix = [
      {
        name: SAMPLE_COLUMN_LABEL,
        extractValue(dataTableRow: Row): string {
          const stageRunSample = dataTableRow.stageRunSampleId
            ? stageRunSampleById[dataTableRow.stageRunSampleId]
            : null;
          return stageRunSample ? stageRunSample.name : '';
        },
      },
      {
        name: 'Stage Run',
        extractValue(dataTableRow: Row): string {
          const stageRun = _.findWhere(stageRuns, {id: dataTableRow.stageRunId});
          return stageRun ? stageRun.name : '';
        },
      },
    ];
  } else if (tableType === DATA_TABLE_TYPE.BATCH_LINK_TABLE) {
    columnsToAffix = [
      {
        name: 'Batch ID',
        extractValue(dataTableRow: Row): string {
          const batch = dataTableRow.batchId ? batchesById[dataTableRow.batchId] : null;
          return batch ? batch.name : '';
        },
      },
    ];
  } else if (tableType === DATA_TABLE_TYPE.FILE_LINK_TABLE) {
    columnsToAffix = [
      {
        name: 'Entity ID',
        extractValue(dataTableRow: Row): string {
          const fileSummary = dataTableRow.fileId ? fileSummariesById[dataTableRow.fileId] : null;
          return fileSummary ? fileSummary.name : '';
        },
      },
    ];
  } else {
    columnsToAffix = [];
  }

  return columnsToAffix;
}

/**
 * Convert a data table to put on the clipboard. This requires mucking with the
 * actual table contents so that we can add artificial columns to the left with the row key and
 * an artificial row at the top with the column title.
 */
export function convertDataTableForExport(
  dataTable: DataTable,
  evaluatedTableMapFromRuntimeSnapshot: OutputTableMap,
  fullTableMap: FullTableMap,
  batchesById: {[batchId: string]: Batch},
  fileSummariesById: {[bioentityId: string]: BioentitySummary},
  stageRuns?: Array<StageRun>,
  stageRunSampleById?: {[stageRunSampleId: string]: StageRunSample}
): {fullTable: Table; computedTable: Array<Array<EvaluatedCell>>} {
  const tableName = getDataTableName(dataTable);
  const tableType = getDataTableConfigType(dataTable.config);
  const baseComputedTable = evaluatedTableMapFromRuntimeSnapshot[tableName];

  const columnsToAffix = _getDataTableColumnsToAffixForExport({
    tableType,
    stageRuns,
    stageRunSampleById,
    batchesById,
    fileSummariesById,
  });

  // Fill in values for the header row and the key column(s).
  // Insert individual columns in a loop to avoid issues when inserting 0 columns or inserting
  // multiple columns into a table with no columns.
  for (let i = 0; i < columnsToAffix.length; i++) {
    fullTableMap = wrapTableOpForSingleDoc((table, documentName) =>
      insertColumnsLeft(table, documentName, 0, 0)
    )(fullTableMap, {}, tableName).tables;
  }
  fullTableMap = wrapTableOpForSingleDoc((table, documentName) => insertRowsAbove(table, documentName, 0, 0))(
    fullTableMap,
    {},
    tableName
  ).tables;

  // Fill in values for the header row and the key column(s).
  fullTableMap = reactUpdate(fullTableMap, {
    [tableName]: {
      $apply: (table: Table) => {
        table = TableUtil.mapCellText(table, (originalCellText: string, i: number, j: number) => {
          if (i === 0) {
            return j < columnsToAffix.length
              ? columnsToAffix[j].name
              : dataTable.config.columns[j - columnsToAffix.length].name;
          } else if (
            j < columnsToAffix.length &&
            // Handle empty data tables where the mediocre table has a dummy row for the empty state.
            i - 1 < dataTable.rows.length
          ) {
            return columnsToAffix[j].extractValue(dataTable.rows[i - 1]);
          } else {
            return originalCellText;
          }
        });
        return {
          ...table,
          // The column labels were already added as fake rows,
          // so set them to null to avoid exporting them twice
          columnLabels: table.columnLabels.map(() => null),
        };
      },
    },
  });

  // Generate evaluated cells for the newly added cells, and merge them into baseComputedTable.
  const fullTable = fullTableMap[tableName];
  const {table} = fullTable;
  const computedTable = produce(baseComputedTable, (computedTable: EvaluatedTable) => {
    for (let i = 0; i < table.length; i++) {
      // Affix header row to exported table
      if (i === 0) {
        const columnNames: Array<EvaluatedCell> = [];
        for (let j = 0; j < table[0].length; j++) {
          columnNames.push({
            value: toStringTypedValue(table[0][j].text),
            locked: false,
            selector: null,
          });
        }
        computedTable.unshift(columnNames);
        continue;
      }

      // Affix Sample ID column(s) to exported table
      const newCells: Array<EvaluatedCell> = [];
      for (let j = 0; j < columnsToAffix.length; j++) {
        // Handle empty data tables where the mediocre table has a dummy row for the empty state.
        const rawValue =
          // `i - 1` is to account for the newly affixed header row
          i - 1 < dataTable.rows.length
            ? columnsToAffix[j].extractValue(dataTable.rows[i - 1])
            : table[i][j].text;
        newCells.push({
          value: toStringTypedValue(rawValue),
          locked: false,
          selector: null,
        });
      }
      computedTable[i].unshift(...newCells);
    }
  });

  return {fullTable, computedTable};
}

export function convertDataTableConfigToTable(
  dataTableConfig: DataTableConfig,
  indicateCustomColumns: boolean,
  {
    alwaysAllowColumnRename,
    columnTypesByCid,
  }: {alwaysAllowColumnRename?: boolean; columnTypesByCid?: {[cid: string]: string}} = {}
): Table {
  const {columns} = dataTableConfig;
  return _convertToTable(dataTableConfig, {
    rowLabels: dataTableConfig.linkType ? ['SAMPLE ID'] : null,
    table: [columns.map((column) => column.defaultValue)],
    allowRowRemoval: false,
    allowNoRows: false,
    columnTypesByCid,
    indicateCustomColumns,
    provenanceUuid: dataTableConfig.provenanceUuid,
  });
}

export function convertDataTableConfigToStandalone(dataTableConfig: DataTableConfig): DataTableConfig {
  return {
    ...dataTableConfig,
    isStandalone: true,
    isWatchingInputTable: false,
    watchedRegistrationProvenanceUuids: [],
  };
}

function findUnusedColumnName(
  baseName: string,
  dataTableConfig: DataTableConfig,
  newColumnNames: Set<string>
): string {
  const columnNames = dataTableConfig.columns.map((col) => col.name);
  let newName = baseName;
  let suffix = 2;
  while (columnNames.includes(newName) || newColumnNames.has(newName)) {
    newName = baseName + suffix;
    suffix++;
  }
  newColumnNames.add(newName);
  return newName;
}

export function mergeTableUpdateIntoDataTableConfig(
  dataTableConfig: DataTableConfig,
  table: TableData
): DataTableConfig {
  const newColumnNames = new Set<string>();
  return reactUpdate(dataTableConfig, {
    name: {$set: table.name},
    columns: {
      $set: table.columnConstraints.map((columnConstraint, i) => {
        const isNew = !columnConstraint || !columnConstraint.structuredTableColumn;
        const field = isNew
          ? null
          : _.findWhere(dataTableConfig.assaySchema.fields, {
              id: (columnConstraint.structuredTableColumn as TagValueStructuredTableColumn).tagSchemaFieldId,
            });
        const updatedColumnConstraint = isNew
          ? {
              provenanceUuid: uuidv4(),
              columnType: TableColumnType.STRING,
              name: findUnusedColumnName('Field', dataTableConfig, newColumnNames),
              cid: uniqueId('dtcol'),
              isReported: true,
              tagSchemaFieldId: null,
              selector: null,
            }
          : {
              ...columnConstraint.structuredTableColumn!,
              name:
                // TODO(BNCH-7596): Stop using `columnLabels` to populate the column name that is
                // sent to the server
                table.columnLabels[i] ||
                (field ? field.displayName : findUnusedColumnName('Field', dataTableConfig, newColumnNames)),
            };
        return {
          ...updatedColumnConstraint,
          defaultValue: table.table[0][i],
          width: table.widths[i],
        } as TagValueStructuredTableColumn;
      }),
    },
  });
}

export function mergeTableUpdateIntoDataTable(dataTable: DataTable, table: TableData): DataTable {
  const configTable = reactUpdate(table, {
    table: {
      $set: [
        table.columnConstraints.map((constraint) =>
          constraint ? constraint.structuredTableColumn!.defaultValue : DEFAULT_DEFAULT_VALUE
        ),
      ],
    },
  });
  const newConfig = mergeTableUpdateIntoDataTableConfig(dataTable.config, configTable);
  const rows = table.rowData!.map((rowData, i) => ({
    ...omit(rowData, ...ROW_UI_PROPERTIES),
    cells: table.table[i].map((cell) => omit(cell, ...CELL_UI_PROPERTIES)),
  }));
  return reactUpdate(dataTable, {
    config: {$set: newConfig},
    // @ts-expect-error: Fixme (Typescript 4.8 upgrade)
    rows: {$set: rows},
  });
}

// TODO(saif): Pull into util module and share with NoteUtil
function _addCid(item: {id: string; cid?: string}, prefix: string, cidIdMap: CidIdMap): void {
  assert(item.cid == null, 'Item already has a cid');
  item.cid = uniqueId(prefix);
  cidIdMap[item.cid] = item.id;
  // @ts-expect-error: id is required in the parameter, but removed for output
  delete item.id;
}

/**
 * Associate a DataTableConfig with a DataTableSchema using the published DataTableConfig as a template.
 *
 * The resulting dataTableConfig will have its columns/schema updated, but will preserve the existing
 * name and watched sources, as the published config defaults don't apply for an existing table.
 */
export function setSchemaForDataTableConfig(
  dataTableConfig: DataTableConfig,
  publishedDataTableConfig: FromServerDataTableConfig,
  cidIdMap: CidIdMap
): DataTableConfig {
  const newConfig = deepCopy(publishedDataTableConfig);
  assert(newConfig.columns.length === dataTableConfig.columns.length, 'DataTable column count changed');
  prepareDataTableConfigForStore(newConfig, cidIdMap);
  const {name, isStandalone, isWatchingInputTable, watchedRegistrationProvenanceUuids} = dataTableConfig;
  // prepareDataTableConfigForStore has converted newConfig to a DataTableConfig
  return reactUpdate(newConfig as unknown as DataTableConfig, {
    $merge: {name, isStandalone, isWatchingInputTable, watchedRegistrationProvenanceUuids},
  });
}

export function setSchemaForDataTable(
  dataTable: DataTable,
  publishedDataTableConfig: FromServerDataTableConfig,
  cidIdMap: CidIdMap
): DataTable {
  return reactUpdate(dataTable, {
    config: {
      $apply: (config: DataTableConfig) =>
        setSchemaForDataTableConfig(config, publishedDataTableConfig, cidIdMap),
    },
  });
}

export function cloneAndPrepareDataTableConfig(
  serverDataTableConfig: FromServerDataTableConfig,
  isStandalone: boolean,
  isWatchingInputTable: boolean,
  watchedRegistrationProvenanceUuids: Array<string>,
  cidIdMap: CidIdMap = {}
): DataTableConfig {
  const preparedConfig = deepCopy(serverDataTableConfig);
  prepareDataTableConfigForStore(preparedConfig, cidIdMap);
  // prepareDataTableConfigForStore has converted preparedConfig to a DataTableConfig
  return reactUpdate(preparedConfig as unknown as DataTableConfig, {
    $merge: {
      isStandalone,
      isWatchingInputTable,
      watchedRegistrationProvenanceUuids,
    },
  });
}

export function prepareDataTableConfigForSave(
  dataTableConfig: DataTableConfig,
  cidIdMap: CidIdMap
): ToServerDataTableConfig {
  const columns = dataTableConfig.columns.map((column) => ({...column, id: cidIdMap[column.cid] as number}));
  return {
    ...dataTableConfig,
    id: cidIdMap[dataTableConfig.cid] as string,
    columns,
  };
}

export function prepareDataTableForStore(serverDataTable: FromServerDataTable, cidIdMap: CidIdMap): void {
  _addCid(serverDataTable, 'dt', cidIdMap);
  prepareDataTableConfigForStore(serverDataTable.config, cidIdMap);
  serverDataTable.rows.forEach((row) => {
    // Old data tables do not have an euid, which will cause the server to delete / recreate the associated
    // DataTableRecord. To avoid this, we have the client generate the missing euid.
    if (!row.euid) {
      row.euid = uuidv4();
    }
  });
}

export function prepareDataTableForSave(dataTable: DataTable, cidIdMap: CidIdMap): ToServerDataTable {
  return {
    ...dataTable,
    id: cidIdMap[dataTable.cid] as string,
    config: prepareDataTableConfigForSave(dataTable.config, cidIdMap),
  };
}

export function getBatchIds(dataTable: DataTable): Array<string> {
  return dataTable.rows.map((row) => row.batchId).filter((batchId) => batchId != null);
}

export function getFileIds(dataTable: DataTable): Array<string> {
  return dataTable.rows.map((row) => row.fileId).filter((fileId) => fileId != null);
}

export function inlineErrorsIntoCells(
  computedTable: Array<Array<EvaluatedCell>>,
  dataTable: DataTable,
  errorsByRecordId: {[recordId: string]: {[fieldId: string]: string}}
): Array<Array<EvaluatedCell>> {
  const errorsByIndex: {[index: string]: string} = {};
  const key = (i: number, j: number): string => `${i}_${j}`;

  // Convert recordId/tagSchemaFieldId into the cell index in computedTable
  dataTable.rows.forEach((row, i) => {
    const errorsByFieldId = errorsByRecordId[row.euid] || {};
    row.cells.forEach((cell, j) => {
      const fieldId = dataTable.config.columns[j].tagSchemaFieldId;
      const error = fieldId && errorsByFieldId[fieldId];
      if (error) {
        errorsByIndex[key(i, j)] = error;
      }
    });
  });

  // Merge errors into appropriate cells, preserving existing error if it already had one.
  return computedTable.map((row, i) =>
    row.map((cell, j) => {
      const error = errorsByIndex[key(i, j)];
      if (cell.error || !error) {
        return cell;
      }
      return {
        ...cell,
        error: {
          type: 'Validation error',
          message: error,
        },
      };
    })
  );
}

/**
 * Return an immutable copy of dataTable with cachedValue filled in for every cell.
 */
export function populateDataTableComputedValues(
  dataTable: DataTable,
  computedTable: Array<Array<EvaluatedCell>>
): DataTable {
  return {
    ...dataTable,
    rows: dataTable.rows.map((row, i) => ({
      ...row,
      cells: row.cells.map((cell, j) => ({
        ...cell,
        cachedValue: formatTypedValue(computedTable[i][j].value, cell),
      })),
    })),
  };
}

export function getInsertableColumnInfos(
  dataTableConfig: DataTableConfig,
  selectorsById: {[selectorId: string]: SchemaFieldSelector},
  forRun: boolean = false
): Array<InsertableColumnInfo> {
  const {assaySchema, columns} = dataTableConfig;
  // We sort the unused schema fields alphabetically, to make it easier for users to re-insert a particular
  // column by name.
  const unusedSchemaFields = _.sortBy(getUnusedSchemaFields(assaySchema, columns), 'displayName').filter(
    (field) => (forRun ? true : isFieldTypeSupportedForManualEntry(field.fieldType))
  );
  if (unusedSchemaFields.length === 0) {
    return [];
  }
  return _.compact([
    !forRun ? {columnLabels: 'New (Custom Column)'} : null,
    ...unusedSchemaFields.map((field: SchemaField) => ({
      columnLabels: field.displayName,
      columnConstraints: {
        structuredTableColumn: makeSchemaFieldStructuredColumn(
          field,
          selectorsById,
          undefined,
          assaySchema.allFields
        ),
      },
    })),
  ]);
}
